/* eslint-disable @typescript-eslint/no-explicit-any */
import { Show, createEffect, createMemo, createSignal, on } from "solid-js";
import type { JSX } from "solid-js";

import { Loading } from "@/components/Base/Loading/Loading";
import { useInView } from "@/hooks/useInView";
import { t } from "@/i18n";
import { isMobile } from "@/lib/isMobile";

const List = (props: {
  loadMore: (value?: any) => Promise<void>;
  finish: boolean;
  finishText?: string;
  noShowFinishText?: boolean;
  children: JSX.Element;
  totalCount?: number;
  currentCount?: number;
  apiLoading?: boolean;
}) => {
  const [btnMore, setBtnMore] = createSignal(false);
  const [loading, setLoading] = createSignal(false);
  const [canLoadMore, setCanLoadMore] = createSignal(true);
  const { inView, setRef } = useInView({
    root: null,
    rootMargin: "200px 0px",
    threshold: 0.1,
  });

  createEffect(
    on(
      createMemo(
        () => btnMore() && !loading() && !props.finish && canLoadMore(),
      ),
      (boolean) => {
        if (boolean) {
          setLoading(true);
          setCanLoadMore(false);
          setBtnMore(true);

          props
            .loadMore()
            .catch((err) => console.error("Load more failed:", err))
            .finally(() => {
              setLoading(false);
              setBtnMore(false);
              setTimeout(() => setCanLoadMore(true), 200);
            });
        }
      },
    ),
  );

  return (
    <div class="relative min-h-[45vh]">
      {props.children}

      <div ref={setRef} class="h-[1px] w-full" />

      {/* 优先显示 apiLoading，当 apiLoading 为 true 时不显示 loadMore 的 loading */}
      <Show when={props.apiLoading} fallback={<></>}>
        <div
          class={`${isMobile() ? "pt-[50%]" : "pt-[160px]"} relative flex h-[60px] w-full justify-center py-[18px]`}
        >
          <Loading />
        </div>
      </Show>

      {/* 只有在没有 apiLoading 时才显示 loadMore 的 loading */}
      <Show
        when={!props.apiLoading && !props.finish && loading()}
        fallback={<></>}
      >
        <div class="relative flex h-[60px] w-full justify-center py-[18px]">
          <Loading />
        </div>
      </Show>

      <Show
        when={
          !props.noShowFinishText &&
          !loading() &&
          !props.apiLoading &&
          props?.totalCount
        }
        fallback={<></>}
      >
        <div
          class="text-general-darkzi flex justify-center font-[500]"
          classList={{
            "text-[14px] leading-[15px] pt-[24px] pb-[12px]": isMobile(),
            "text-[16px] leading-[17px] py-[18px]": !isMobile(),
          }}
        >
          {props.finishText ??
            t("home.displaying_games", {
              val1: props?.currentCount || 0,
              val2: props?.totalCount || 0,
            })}
        </div>
      </Show>

      <Show
        when={props?.currentCount !== props?.totalCount && !props.apiLoading}
        fallback={<></>}
      >
        <div class="text-general-darkzi mb-[24px] flex justify-center">
          <div>
            <button
              onClick={() => {
                setBtnMore(true);
              }}
              class="bg-home-bg6 text-general-light flex h-[41px] w-[192px] flex-1 cursor-pointer items-center justify-center rounded-md leading-[17px]"
              classList={{
                "text-[12px] leading-[17px] font-[800]": isMobile(),
                "text-[14px] leading-[17px] font-[700]": !isMobile(),
              }}
            >
              {t("common.loadmore")}
            </button>
          </div>
        </div>
      </Show>
    </div>
  );
};

export default List;
